import React, { useEffect, useState } from "react";
import { ScalableImage, Typography, useDevice } from "design_system/src";
import { Link, useNavigate } from "react-router-dom";
import useAlliance from "hooks/useAlliance";
import { formatPrice } from "helpers/formatPrice";
import { prices } from "helpers/prices";
import { getSpanishColorName } from "helpers/translateColorValues";
import { ReactComponent as Arrow } from "design_system/src/static/icons/back.svg";
import { LenderEnum } from "models/inventory/enums/vehicle.enums";
import { TVehicle } from "../../models/inventory/interfaces/vehicle.interface";
import styles from "./CardVehicle.module.scss";
import HelperCard from "./HelperCard";

type CardLabelProps = {
  className?: string;
};

const CardLabel: React.FC<CardLabelProps> = ({ className, children }) => {
  return <div className="display_flex flex_center">
    <div className={`${styles.CardLabel} ${className} br_md p_y_xxxs p_x_sm`}>
      <Typography
        scale="xsmall"
        scaleMobile="small"
        weight="400"
      >
        {children}
      </Typography>
    </div>
  </div>;
};

type CardLabelsProps = {
  vehicle?: TVehicle;
  alliance?: any;
  className?: string;
};

const CardLabels: React.FC<CardLabelsProps> = ({ vehicle, alliance, className }) => {
  return (
    <div
      className={`display_flex flex_wrap flex_gap_xxs${className ? ` ${className}` : ""}`}
    >
      {vehicle?.hasDiscount() && (
        <CardLabel
          className={`${styles.CardLabelPrimaryGhost}${alliance ? ` ${styles["CardLabelPrimaryGhost--alliance"]}` : ""}`}
        >
          {vehicle?.activeDiscount?.type === "percentage" && `-${Math.round((vehicle?.activeDiscount?.value ?? 0) * 100)}% Desc.`}
          {vehicle?.activeDiscount?.type === "value" && `-$${vehicle?.activeDiscount?.value} Desc.`}
        </CardLabel>
      )}
      {vehicle?.isNewVehicle === true && vehicle?.isPresale === true && (
        <CardLabel
          className={`${styles.CardLabelPrimary}${alliance ? ` ${styles["CardLabelPrimary--alliance"]}` : ""}`}
        >
          Pre-venta
        </CardLabel>
      )}
      {vehicle?.isNewVehicle === true && (
        <CardLabel
          className={`${styles.CardLabelPrimary}${alliance ? ` ${styles["CardLabelPrimary--alliance"]}` : ""}`}
        >
          Nueva
        </CardLabel>
      )}
      {vehicle?.isPlatedVehicle === true && (
        <CardLabel
          className={`${styles.CardLabelPrimary}${alliance ? ` ${styles["CardLabelPrimary--alliance"]}` : ""}`}
        >
          Emplacada
        </CardLabel>
      )}
      {vehicle?.maintenanceDays === 0 && (
        <CardLabel
          className={`${styles.CardLabelSecondary}${alliance ? ` ${styles["CardLabelSecondary--alliance"]}` : ""}`}
        >
          Entrega inmediata
        </CardLabel>
      )}
    </div>
  );
};

type CardVehicleProps = {
  vehicle?: TVehicle;
  isHelperCard: boolean;
  triggerCard?: any;
};

const CardVehicle: React.FC<CardVehicleProps> = ({
  vehicle,
  isHelperCard,
  triggerCard,
}) => {
  const { alliance, catalogBaseUrl } = useAlliance();
  const { isMobile, isTablet, isDesktop } = useDevice();

  const navigate = useNavigate();

  const [isHovering, setIsHovering] = useState(false);
  const [loanTime, setLoanTime] = useState<number>();

  useEffect(() => {
    if (vehicle) {
      const loan =
        vehicle?.salePrices?.[vehicle.salePrices.length - 1]?.weeks !== undefined
          ? vehicle.salePrices[vehicle.salePrices.length - 1].weeks
          : vehicle.salePrices[0].weeks;
      setLoanTime(loan);
    }
  }, [vehicle]);

  const handleCity = (city: string): string => {
    switch (city) {
      case "Guadalajara":
        return "GDL";

      case "Ciudad de México":
        return "CDMX";

      default:
        return "CDMX";
    }
  };

  const handleMouseOver = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  return (
    // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
    <div
      className={`${styles.CardVehicle}${isHovering ? ` ${styles["CardVehicle--hovering"]}` : ""}`}
      style={{ height: "100%" }}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
    >
      {isHelperCard && (
        <HelperCard
          triggerCard={triggerCard}
          isHovering={isHovering}
          isMobile={isMobile}
        />
      )}

      {!isHelperCard && vehicle && (
        <Link to={`${catalogBaseUrl}/${vehicle?.internalId}`} className="custom_a">
          <div className="display_flex flex_col_desktop pos_relative">
            <div className={`${styles.CardVehicleImgBox}`}>
              <figure className={`${styles.CardVehicleFigure} m_none pos_relative`}>
                <ScalableImage
                  className={styles.CardVehicleImg}
                  src={vehicle?.images && vehicle?.images[0]?.url}
                  alt={`Moto ${vehicle?.brand.name} ${vehicle?.model.name}, ${vehicle?.isNewVehicle ? "nueva" : "seminueva"} de ${vehicle?.cylindersCapacity}cc, año ${vehicle?.year}, color ${getSpanishColorName(vehicle?.color ?? "black").toLocaleLowerCase()} en venta en ${vehicle?.location.city.name} desde $${formatPrice(prices(vehicle?.getWeeklyPrice(loanTime)), "")} semanales`}
                  desktopWidth={10}
                  mobileWidth={10}
                />
                {!isMobile && <div className="pos_absolute p_y_xs p_x_sm" style={{ top: 0, left: 0 }}>
                  <CardLabels vehicle={vehicle} alliance={alliance} />
                </div>}

                {(
                  (vehicle?.lenders ?? []).includes(LenderEnum.Atrato) ||
                  (vehicle?.lenders ?? []).includes(LenderEnum.Maxikash)
                ) && (
                  <div
                    className={`pos_absolute ${ isMobile ? "m_t_lg m_l_lg" : "p_y_xs p_x_sm"}`}
                    style={{
                      top: isMobile ? 0 : "unset",
                      bottom: isMobile ? "unset" : 0,
                      left: isMobile ? 0 : "unset",
                      right: isMobile ? "unset" : 0,
                    }}
                  >
                    <ScalableImage
                      className="h_15_px"
                      src="https://ik.imagekit.io/ozon/Homepage/Logos/logo_alianza_walmart_cashi_bodega_aurrera_xsmall"
                      alt="Logo de cashi - walmart"
                      desktopWidth={10}
                      mobileWidth={10}
                    />
                  </div>
                )}
              </figure>
            </div>
            <div className={`${styles.CardVehicleInfo} display_flex flex_col p_x_md p_b_md`}>
              {isMobile && (
                <CardLabels
                  vehicle={vehicle}
                  alliance={alliance}
                  className="p_t_md p_r_md"
                />
              )}
              <div className="p_t_xs p_t_md_desktop">
                {isMobile && (
                  <Typography
                    scale="medium"
                    weight="600"
                    className="m_none text_neutral_800"
                    component="h3"
                  >
                    {vehicle?.brand?.name} {`${vehicle?.model?.name} ${vehicle?.cylindersCapacity}${vehicle?.suffix ? vehicle.suffix : ""}`}
                  </Typography>
                )}
                {!isMobile && (
                  <>
                    <Typography
                      scale="medium"
                      weight="400"
                      className="m_none text_neutral_800"
                      component="h3"
                    >
                      {vehicle?.brand?.name}
                    </Typography>
                    <Typography
                      scale="medium"
                      weight="400"
                      className="m_none"
                      component="h3"
                    >
                      {`${vehicle?.model?.name} ${vehicle?.cylindersCapacity
                        }${vehicle?.suffix ? vehicle.suffix : ""}`}
                    </Typography>
                  </>
                )}
              </div>

              <div className="p_t_xxs text_neutral_800">
                <Typography
                  scale="small"
                  weight="400"
                  className="p_r_xxs m_r_xxs"
                >
                  {vehicle?.year} &bull; {vehicle?.mileage === 0 && !vehicle?.isNewVehicle
                    ? "KM pendiente"
                    : `${vehicle?.mileage} Km`}
                </Typography>
              </div>

              <div className="w_100_per border_1 border_solid border_neutral_400 m_t_xs"/>

              <div className="m_t_xs w_100_per">
                <Typography
                  scale="xsmall"
                  scaleMobile="small"
                  weight="400"
                  component="p"
                  className="m_none text_neutral_800"
                >
                  Precio:
                </Typography>
                <Typography
                  scale="heading3"
                  weight="600"
                  scaleMobile="heading4"
                  component="p"
                  className="m_none"
                >
                  ${formatPrice(prices(vehicle?.marketPrice), "")}
                </Typography>
              </div>

              <div className="m_t_xs w_100_per">
                <Typography
                  scale="xsmall"
                  scaleMobile="small"
                  weight="400"
                  component="p"
                  className="m_none"
                >
                  Financiamiento con:
                </Typography>
                <div className="display_flex flex_wrap">
                  {vehicle?.lenderImages?.map(({ url, alt, width, height}) => (
                    <ScalableImage
                      key={alt}
                      className={styles.CardLender}
                      src={url}
                      alt={alt}
                      desktopWidth={10}
                      mobileWidth={10}
                      style={{ width, height }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Link>
      )}
    </div>
  );
};

export default CardVehicle;